import { useEffect, useState } from "react";
import {
  Box,
  Button,
  Group,
  Loader,
  Modal,
  Paper,
  Stack,
  Text,
  TextInput,
  UnstyledButton,
} from "@mantine/core";
import { useDebouncedValue } from "@mantine/hooks";
import { notifications } from "@mantine/notifications";
import { IconArrowLeft, IconSearch, IconSend } from "@tabler/icons-react";
import { api } from "../../lib";
import {
  useActiveDeliveryMethodsForCase,
  useQueueManualDelivery,
} from "../../hooks/useWorkflows";
import { DeliveryNoMethodsContent } from "./DeliveryNoMethodsContent";
import { DeliveryMethodChecklistContent } from "./DeliveryMethodChecklistContent";

interface CaseResult {
  id: string;
  caseNumber: string;
  physicianId: string | null;
  sendingPracticeId: string | null;
  patient?: { firstName?: string; lastName?: string } | null;
  physician?: { firstName?: string; lastName?: string } | null;
  sendingPractice?: { name?: string } | null;
}

interface CreateDeliveryModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function CreateDeliveryModal({
  opened,
  onClose,
  onSuccess,
}: CreateDeliveryModalProps) {
  const [step, setStep] = useState<"search" | "methods">("search");
  const [caseSearch, setCaseSearch] = useState("");
  const [debouncedSearch] = useDebouncedValue(caseSearch, 400);
  const [caseResults, setCaseResults] = useState<CaseResult[]>([]);
  const [caseSearchLoading, setCaseSearchLoading] = useState(false);
  const [selectedCase, setSelectedCase] = useState<CaseResult | null>(null);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  const { data: methods, isLoading: methodsLoading } =
    useActiveDeliveryMethodsForCase(
      step === "methods" && selectedCase ? selectedCase.id : undefined
    );
  const queueMutation = useQueueManualDelivery();

  // Search eligible cases
  useEffect(() => {
    if (!debouncedSearch.trim() || step !== "search") return;

    setCaseSearchLoading(true);
    api
      .get<{ data: CaseResult[]; total: number }>(
        `/cases?eligibleForSendOut=true&search=${encodeURIComponent(
          debouncedSearch
        )}&limit=10`
      )
      .then((result) => {
        const items = Array.isArray(result)
          ? result
          : (result as { data: CaseResult[] })?.data || [];
        setCaseResults(items as CaseResult[]);
      })
      .catch(() => setCaseResults([]))
      .finally(() => setCaseSearchLoading(false));
  }, [debouncedSearch, step]);

  // Pre-select all methods when they load
  useEffect(() => {
    if (methods) {
      setSelectedIds(methods.map((m) => m.id));
    }
  }, [methods]);

  const handleCaseSelect = (c: CaseResult) => {
    setSelectedCase(c);
    setCaseResults([]);
    setStep("methods");
  };

  const handleBack = () => {
    setStep("search");
    setSelectedCase(null);
    setSelectedIds([]);
  };

  const handleClose = () => {
    setStep("search");
    setCaseSearch("");
    setCaseResults([]);
    setSelectedCase(null);
    setSelectedIds([]);
    onClose();
  };

  const handleSend = async () => {
    if (!selectedCase || selectedIds.length === 0) return;
    try {
      const result = await queueMutation.mutateAsync({
        caseId: selectedCase.id,
        deliveryConfigIds: selectedIds,
      });
      const count = result.queued?.length ?? selectedIds.length;
      notifications.show({
        color: "green",
        title: "Report Queued",
        message: `${count} delivery item${
          count !== 1 ? "s" : ""
        } queued for processing.`,
      });
      onSuccess?.();
      handleClose();
    } catch {
      notifications.show({
        color: "red",
        title: "Send Failed",
        message: "Failed to queue report deliveries. Please try again.",
      });
    }
  };

  const patientName = selectedCase?.patient
    ? `${selectedCase.patient.firstName || ""} ${
        selectedCase.patient.lastName || ""
      }`.trim()
    : null;
  const physicianName = selectedCase?.physician
    ? `${selectedCase.physician.firstName || ""} ${
        selectedCase.physician.lastName || ""
      }`.trim()
    : null;
  const practiceName = selectedCase?.sendingPractice?.name || null;

  const hasNoMethods = !methodsLoading && methods && methods.length === 0;
  const hasSingleMethod = !methodsLoading && methods && methods.length === 1;
  const hasMultipleMethods = !methodsLoading && methods && methods.length >= 2;

  return (
    <Modal.Root opened={opened} onClose={handleClose} size="md">
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>
            {step === "search"
              ? "Create New Delivery"
              : "Select Delivery Methods"}
          </Modal.Title>
          <Modal.CloseButton />
        </Modal.Header>
        <Modal.Body>
          {step === "search" && (
            <Stack gap="md">
              <Text size="sm" c="dimmed">
                Search for a signed-off case to queue a report delivery.
              </Text>
              <TextInput
                placeholder="Search by case number or patient name..."
                leftSection={<IconSearch size={16} />}
                value={caseSearch}
                onChange={(e) => setCaseSearch(e.currentTarget.value)}
                rightSection={
                  caseSearchLoading ? <Loader size="xs" /> : undefined
                }
                autoFocus
              />
              {caseResults.length > 0 && (
                <Stack gap={4}>
                  {caseResults.map((c) => {
                    const pt = c.patient
                      ? `${c.patient.firstName || ""} ${
                          c.patient.lastName || ""
                        }`.trim()
                      : null;
                    const ph = c.physician
                      ? `${c.physician.firstName || ""} ${
                          c.physician.lastName || ""
                        }`.trim()
                      : null;
                    const pr = c.sendingPractice?.name || null;
                    return (
                      <UnstyledButton
                        key={c.id}
                        onClick={() => handleCaseSelect(c)}
                      >
                        <Paper
                          withBorder
                          p="sm"
                          style={{ cursor: "pointer" }}
                          styles={{
                            root: {
                              "&:hover": {
                                backgroundColor: "var(--mantine-color-gray-0)",
                              },
                            },
                          }}
                        >
                          <Stack gap={2}>
                            <Text size="sm" fw={600}>
                              {c.caseNumber}
                            </Text>
                            {pt && (
                              <Text size="xs" c="dimmed">
                                {pt}
                              </Text>
                            )}
                            {(ph || pr) && (
                              <Text size="xs" c="dimmed">
                                {[ph, pr].filter(Boolean).join(" — ")}
                              </Text>
                            )}
                          </Stack>
                        </Paper>
                      </UnstyledButton>
                    );
                  })}
                </Stack>
              )}
              {debouncedSearch.trim() &&
                !caseSearchLoading &&
                caseResults.length === 0 && (
                  <Text size="sm" c="dimmed" ta="center" py="sm">
                    No signed-off cases found matching &quot;{debouncedSearch}
                    &quot;
                  </Text>
                )}
            </Stack>
          )}

          {step === "methods" && selectedCase && (
            <Stack gap="lg">
              {/* Case summary */}
              <Box>
                <Text size="xs" fw={600} tt="uppercase" c="dimmed" mb={4}>
                  Selected Case
                </Text>
                <Paper withBorder p="sm">
                  <Stack gap={2}>
                    <Text size="sm" fw={600}>
                      {selectedCase.caseNumber}
                    </Text>
                    {patientName && (
                      <Text size="xs" c="dimmed">
                        {patientName}
                      </Text>
                    )}
                    {(physicianName || practiceName) && (
                      <Text size="xs" c="dimmed">
                        {[physicianName, practiceName]
                          .filter(Boolean)
                          .join(" — ")}
                      </Text>
                    )}
                  </Stack>
                </Paper>
              </Box>

              {methodsLoading && (
                <Group justify="center" py="sm">
                  <Loader size="sm" />
                  <Text size="sm" c="dimmed">
                    Loading delivery methods...
                  </Text>
                </Group>
              )}

              {hasNoMethods && (
                <DeliveryNoMethodsContent
                  physicianId={selectedCase.physicianId}
                  practiceId={selectedCase.sendingPracticeId}
                />
              )}

              {hasSingleMethod && methods && (
                <Stack gap="sm">
                  <Text size="sm" c="dimmed">
                    This case has one delivery method configured:
                  </Text>
                  <Paper withBorder p="sm">
                    <Stack gap={2}>
                      <Text size="sm" fw={500}>
                        {methods[0].deliveryMethodName}
                      </Text>
                      <Text size="xs" c="dimmed">
                        {methods[0].recipientName}
                        {methods[0].destination
                          ? ` — ${methods[0].destination}`
                          : ""}
                      </Text>
                    </Stack>
                  </Paper>
                </Stack>
              )}

              {hasMultipleMethods && methods && (
                <DeliveryMethodChecklistContent
                  methods={methods}
                  selectedIds={selectedIds}
                  onChange={setSelectedIds}
                />
              )}

              {/* Footer buttons */}
              <Group justify="space-between">
                <Button
                  variant="subtle"
                  leftSection={<IconArrowLeft size={14} />}
                  onClick={handleBack}
                >
                  Back
                </Button>
                {(hasSingleMethod || hasMultipleMethods) && (
                  <Group>
                    <Button variant="default" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      color="gcpBlue"
                      leftSection={<IconSend size={16} />}
                      onClick={handleSend}
                      loading={queueMutation.isPending}
                      disabled={selectedIds.length === 0 && !hasSingleMethod}
                    >
                      Send
                    </Button>
                  </Group>
                )}
              </Group>
            </Stack>
          )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
}
